/**
 * ⚠️  安全关键路径（XSS 防护）
 *
 * 本模块是 {@html ...} 渲染前的唯一过滤层。
 * Rules.svelte 中使用 {@html rulesHtml} 渲染帮助中心文章内容，
 * 安全性完全依赖调用方在赋值前先经过 sanitizeArticleHtml()。
 *
 * 规则：
 *   - 所有来自接口的 HTML 字符串在写入 $state 变量之前，
 *     必须先调用 sanitizeArticleHtml()，不得直接赋值。
 *   - 不要在其他地方绕过此函数直接使用 {@html}。
 *   - 如需新增允许的标签/属性，请评估 XSS 风险后再修改 ALLOWED_TAGS / ALLOWED_ATTR。
 */
import DOMPurify, { type DOMPurify as DOMPurifyType } from "dompurify";

// 创建独立实例，避免全局 hook 污染
const purify: DOMPurifyType = DOMPurify(window);

const ALLOWED_TAGS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "br",
  "hr",
  "ul",
  "ol",
  "li",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "del",
  "ins",
  "span",
  "div",
  "a",
  "img",
  "blockquote",
  "pre",
  "code",
];

// 移除 style 属性，避免 CSS 注入风险（Unicode 转义、env()/calc() 等绕过）
// 样式通过页面 CSS 和 class 控制
const ALLOWED_ATTR = [
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "width",
  "height",
  "class",
  "colspan",
  "rowspan",
];

purify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeName === "A") {
    // 校验 href：只允许 http(s)、相对路径、锚点
    // 显式拒绝 javascript:、data:、blob: 等危险协议（双重保险）
    const href = node.getAttribute("href") || "";
    if (href) {
      const lower = href.toLowerCase().replace(/[\s\x00-\x1f]/g, "");
      const isDangerous =
        lower.startsWith("javascript:") ||
        lower.startsWith("data:") ||
        lower.startsWith("blob:") ||
        lower.startsWith("vbscript:");
      const isAllowed =
        /^https?:\/\//i.test(href) ||
        href.startsWith("#") ||
        href.startsWith("/");
      if (isDangerous || !isAllowed) {
        node.removeAttribute("href");
      }
    }
    // 为 target="_blank" 补 rel="noopener noreferrer"
    if (node.getAttribute("target") === "_blank") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  }
});

export function sanitizeArticleHtml(html: string): string {
  if (!html) return "";
  return purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR: ["style"], // 显式禁止 style，双重保险
  });
}
